import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, normalizeApiError } from '../../lib/apiClient'
import type { ApiResponse } from '../../types'
import Panel from '../../components/cards/Panel'
import PageHeader from '../../components/common/PageHeader'
import ErrorState from '../../components/common/ErrorState'
import EmptyState from '../../components/common/EmptyState'
import { Skeleton } from '../../components/common/Loading'
import BrandingUpload from '../settings/BrandingUpload'
import ProfilePhoto from '../portal/ProfilePhoto'
import { Link } from 'react-router-dom'
import FormModal from '../../components/common/FormModal'
import SiteIdentitySettings from '../settings/SiteIdentitySettings'
type Row = Record<string, unknown> & { id: number }
const masterSchemas: Record<string, Record<string, string>> = {
  departments: {
    name: 'required|string',
    slug: 'nullable|string',
    description: 'nullable|string',
    is_active: 'boolean',
    sort_order: 'integer|min:0',
  },
  services: {
    department_id: 'required|integer|min:1',
    name: 'required|string',
    slug: 'nullable|string',
    description: 'nullable|string',
    is_active: 'boolean',
  },
}
export default function ManagementPage({
  module,
  role = 'admin',
}: {
  module: string
  role?: 'admin' | 'doctor'
}) {
  const [page, setPage] = useState(1),
    [search, setSearch] = useState(''),
    [status, setStatus] = useState(''),
    [editing, setEditing] = useState<Row | null | undefined>(undefined),
    [direction, setDirection] = useState('desc')
  const client = useQueryClient()
  const base = masterSchemas[module]
    ? '/admin/' + module
    : '/' + role + '/manage/' + module
  const schema = useQuery({
    queryKey: ['schema', module],
    queryFn: async () =>
      masterSchemas[module] ??
      (
        await apiClient.get<ApiResponse<Record<string, string>>>(
          '/admin/manage/' + module + '/schema',
        )
      ).data.data,
    enabled: role === 'admin',
  })
  const doctorFields = {
    title: 'string',
    registration_no: 'string',
    qualifications: 'string',
    experience_years: 'integer|min:0',
    bio: 'string',
    phone: 'string',
    timezone: 'timezone',
    default_fee: 'numeric|min:0',
    default_duration_minutes: 'integer|min:5',
    chamber_name: 'string',
    chamber_address: 'string',
    is_published: 'boolean',
  }
  const fields = role === 'doctor' ? doctorFields : (schema.data ?? {})
  const q = useQuery({
    queryKey: ['manage', role, module, page, search, status, direction],
    queryFn: async () =>
      (
        await apiClient.get<ApiResponse<Row[]>>(base, {
          params: { page, search, status: status || undefined, direction },
        })
      ).data,
  })
  const m = useMutation({
    mutationFn: (d: Record<string, unknown>) =>
      editing
        ? apiClient.put(base + '/' + editing.id, d)
        : apiClient.post(base, d),
    onSuccess: () => {
      setEditing(undefined)
      q.refetch()
      client.invalidateQueries({queryKey:['public-settings']})
      client.invalidateQueries({queryKey:['portfolio']})
      client.invalidateQueries({queryKey:['public-doctor']})
    },
  })
  const remove = useMutation({
    mutationFn: (id: number) => apiClient.delete(base + '/' + id),
    onSuccess: () => q.refetch(),
  })
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const d: Record<string, unknown> = {}
    Object.entries(fields).forEach(([k, rule]) => {
      const v = form.get(k)
      d[k] = rule.includes('boolean') ? form.has(k) : v === '' ? null : v
    })
    if (module === 'users' && !editing) {
      d.password = form.get('password')
      d.role = form.get('role')
    }
    m.mutate(d)
  }
  const columns = q.data?.data[0]
    ? Object.keys(q.data.data[0])
        .filter(
          (k) =>
            ![
              'updated_at',
              'deleted_at',
              'created_at',
              'old_values',
              'new_values',
              'ip_address',
              'user_agent',
            ].includes(k),
        )
        .slice(0, 7)
    : []
  const writable = Object.keys(fields).length > 0
  return (
    <div className="page-container">
      {role === 'doctor' && module === 'doctor-profile' && q.data?.data[0] && <ProfilePhoto endpoint={`/doctors/${q.data.data[0].id}/photo`} photoUrl={q.data.data[0].photo_url as string | null} onSaved={() => q.refetch()} />}
      <PageHeader
        title={module.replaceAll('-', ' ')}
        description="Manage records with server validation"
      />
      <Panel title="Records">
        {module === 'appointments' && (
          <Link className="btn btn-primary mb-3" to="/admin/appointments/new">
            Book for patient
          </Link>
        )}
        {['appointments', 'prescriptions', 'patients'].includes(module) &&
          q.data?.data.map((row) => (
            <Link
              className="btn btn-sm btn-outline-secondary me-2 mb-2"
              key={row.id}
              to={'/admin/' + module + '/' + row.id}
            >
              Open #{row.id}
            </Link>
          ))}
        <div className="phase-toolbar mb-3">
          <input
            aria-label="Search records"
            className="form-control"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            placeholder="Search"
          />
          <input
            aria-label="Status filter"
            className="form-control"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            placeholder="Status"
          />
          <select
            aria-label="Sort order"
            className="form-select"
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
          >
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
          {writable &&
            role === 'admin' &&
            !['appointments', 'doctor-profile'].includes(module) && (
              <button
                className="btn btn-primary"
                onClick={() => setEditing(null)}
              >
                Add record
              </button>
            )}
        </div>
        {(m.isError || remove.isError) && (
          <ErrorState
            message={
              Object.values(
                normalizeApiError(m.error ?? remove.error).validation,
              )
                .flat()
                .join(' ') || normalizeApiError(m.error ?? remove.error).message
            }
          />
        )}
        {(m.isSuccess || remove.isSuccess) && (
          <p role="status">Changes saved.</p>
        )}
        {q.isLoading ? (
          <Skeleton lines={6} />
        ) : q.isError ? (
          <ErrorState onRetry={() => q.refetch()} />
        ) : !q.data?.data.length ? (
          <EmptyState title="No records" />
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  {columns.map((k) => (
                    <th key={k}>{k.replaceAll('_', ' ')}</th>
                  ))}
                  {writable && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {q.data.data.map((row) => (
                  <tr key={row.id}>
                    {columns.map((k) => (
                      <td
                        key={k}
                        style={{ maxWidth: 240, overflowWrap: 'anywhere' }}
                      >
                        {typeof row[k] === 'object'
                          ? '—'
                          : String(row[k] ?? '').slice(0, 180)}
                      </td>
                    ))}
                    {writable && (
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setEditing(row)}
                        >
                          Edit
                        </button>
                        {['pages', 'faqs', 'departments', 'services'].includes(
                          module,
                        ) && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => {
                              if (window.confirm('Archive this record?'))
                                remove.mutate(row.id)
                            }}
                          >
                            Archive
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="form-actions">
          <button
            className="btn btn-outline-secondary"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>
          <span>Page {page}</span>
          <button
            className="btn btn-outline-secondary"
            disabled={!q.data?.meta || page >= q.data.meta.last_page}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </Panel>
      {module === 'settings' && <><SiteIdentitySettings /><BrandingUpload /></>}
      {editing !== undefined && (
        <FormModal title={editing ? 'Edit record' : 'New record'} subtitle={`Manage ${module.replaceAll('-', ' ')} details`} onClose={() => setEditing(undefined)}>
          <form onSubmit={submit} key={editing?.id ?? 'new'}>
            <div className="form-grid">
              {Object.entries(fields).map(([k, rule]) => {
                const options = rule
                  .split('|')
                  .find((r) => r.startsWith('in:'))
                  ?.slice(3)
                  .split(',')
                const isNumber =
                  rule.includes('integer') || rule.includes('numeric')
                return (
                  <label key={k}>
                    {k.replaceAll('_', ' ')}
                    {rule.includes('boolean') ? (
                      <input
                        className="form-check-input ms-2"
                        name={k}
                        type="checkbox"
                        defaultChecked={Boolean(editing?.[k] ?? true)}
                      />
                    ) : options ? (
                      <select
                        className="form-select"
                        name={k}
                        defaultValue={String(editing?.[k] ?? options[0])}
                      >
                        {options.map((o) => (
                          <option key={o}>{o}</option>
                        ))}
                      </select>
                    ) : ['bio','content','answer','description','setting_value','message','chamber_address'].includes(k) ? (
                      <textarea className="form-control" name={k} rows={4} required={rule.includes('required')} defaultValue={String(editing?.[k]??'')}/>
                    ) : (
                      <input
                        className="form-control"
                        name={k}
                        required={rule.includes('required')}
                        type={
                          isNumber
                            ? 'number'
                            : k.includes('date')
                              ? 'date'
                              : k.includes('time') && !k.includes('zone')
                                ? 'time'
                                : 'text'
                        }
                        min={isNumber ? 0 : undefined}
                        step={rule.includes('numeric') ? '0.01' : undefined}
                        defaultValue={String(editing?.[k] ?? '').slice(
                          0,
                          k.includes('time') && !k.includes('zone') ? 5 : 20000,
                        )}
                      />
                    )}
                  </label>
                )
              })}
              {module === 'users' && !editing && (
                <>
                  <label>
                    Initial password
                    <input
                      name="password"
                      className="form-control"
                      type="password"
                      required
                      minLength={8}
                    />
                  </label>
                  <label>
                    Role
                    <select name="role" className="form-select">
                      <option>patient</option>
                      <option>doctor</option>
                      <option>admin</option>
                    </select>
                  </label>
                </>
              )}
            </div>
            <div className="form-actions">
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setEditing(undefined)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" disabled={m.isPending}>
                Save
              </button>
            </div>
          </form>
        </FormModal>
      )}
    </div>
  )
}
